import { CONSUMER_STATUS } from "__helpers/constants";

/**
 * The MaxAlly Invite button's label, which reports how far a patient's
 * contacts have got with the invitation.
 *
 *   nobody invited yet      the caller's own wording ("INVITE" on the
 *                           dashboard, "Invite to MaxAlly" elsewhere)
 *   invited, none joined    "Invited (2/5)"  — of all the patient's contacts
 *   at least one joined     "Joined (1/2)"   — of those invited
 *
 * Note the denominator changes with the state: the first asks "how much of
 * this family have we reached", the second "how many of those we reached
 * actually signed up". That is the client's specified behaviour.
 *
 * Shared by the three places the button appears — the dashboard card, and both
 * the single and multi-patient modes of the patient detail screen — so the
 * three cannot drift apart.
 *
 * @param {{totalContacts:number, invitedCount:number, joinedCount:number}|null|undefined} counts
 * @param {string} fallback label to use before counts arrive, or when nobody
 *                 has been invited yet
 */
export function inviteButtonLabel(counts, fallback) {
  const { text, count } = inviteButtonLabelParts(counts, fallback);
  return count ? text + " (" + count + ")" : text;
}

/**
 * Same label split in two — { text: "Joined", count: "5/6" } — for a button
 * that shows the count on its own line (Patient Detail, single-patient mode).
 * count is null whenever the label is the plain fallback.
 */
export function inviteButtonLabelParts(counts, fallback) {
  // No counts loaded yet (or none for this patient) — the plain invitation
  // reads correctly either way, so there is never a flash of "(0/0)".
  if (!counts) return { text: fallback, count: null };

  const total = toCount(counts.totalContacts);
  const invited = toCount(counts.invitedCount);
  const joined = toCount(counts.joinedCount);

  if (joined > 0) return { text: "Joined", count: joined + "/" + invited };
  if (invited > 0) return { text: "Invited", count: invited + "/" + total };
  return { text: fallback, count: null };
}

/**
 * Indexes a counts response by patient id.
 *
 * The endpoint returns a flat array; every screen wants to look one up by
 * patient while rendering a row.
 */
export function indexCountsByPatient(rows) {
  const byPatient = {};
  if (!Array.isArray(rows)) return byPatient;
  rows.forEach(row => {
    if (row && row.patientId != null) byPatient[row.patientId] = row;
  });
  return byPatient;
}

/**
 * Counts derived from an already-loaded contact list.
 *
 * Lets a screen that has fetched the contacts — the dialog does, on open —
 * update its label immediately after an invite, without waiting for another
 * round trip to the counts endpoint.
 *
 * Mirrors the server's rules exactly: invited is any consumer link whatever
 * its status, joined is ACTIVE only, so someone a planner has deactivated
 * still counts as invited but no longer as joined.
 */
export function countsFromContactList(patientId, contacts) {
  const list = Array.isArray(contacts) ? contacts : [];
  return {
    patientId,
    totalContacts: list.length,
    invitedCount: list.filter(c => c && c.isPatientConsumer).length,
    joinedCount: list.filter(
      c =>
        c && c.isPatientConsumer && c.consumerStatus === CONSUMER_STATUS.ACTIVE
    ).length
  };
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
